/**
 * Drawing for the table's tablets: bronze-tinted wood with silver screws, gold lettering,
 * engraved plaques and gemstone buttons. Everything is plain Canvas 2D so the same code renders
 * the texture of an in-world tablet on desktop and in the headset.
 */

export type GemKind = 'amethyst' | 'ruby' | 'emerald' | 'diamond' | 'onyx';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface GemOptions {
  label: string;
  /** Dull stone, no sparkle, dim label. */
  enabled: boolean;
  /** The chosen one of a set: a gold ring around it. */
  active?: boolean;
  /** 0 … 1, a white flash right after a tap. */
  pressed?: number;
  /** Seconds, drives the smoke and the sparkles. */
  time: number;
  /** Stable per-gem number so sparkles do not all twinkle together. */
  seed: number;
  fontSize?: number;
  /** A square, flat indicator rather than a soft, faceted button. */
  flat?: boolean;
  /** Spell points this costs, shown as a badge beside the name; 0 and below show none. */
  cost?: number;
}

const GEM_COLORS: Record<GemKind, { light: string; mid: string; dark: string; glow: string }> = {
  amethyst: { light: '#dfb6ff', mid: '#8b3fd9', dark: '#3a1263', glow: '#f0d4ff' },
  ruby: { light: '#ff9c9c', mid: '#d2142f', dark: '#560511', glow: '#ffc9c9' },
  emerald: { light: '#a9ffcb', mid: '#1ea45e', dark: '#093a20', glow: '#d2ffe4' },
  diamond: { light: '#ffffff', mid: '#cfe4ff', dark: '#7d98b6', glow: '#ffffff' },
  onyx: { light: '#9a9aa6', mid: '#2e2e37', dark: '#060608', glow: '#c4c4d0' },
};

const DULL = { light: '#7b7670', mid: '#4a4642', dark: '#221f1c', glow: '#8a857f' };

export const FONT = "'Trebuchet MS', 'Segoe UI', system-ui, sans-serif";

/** The wood face with a bevelled rim and a screw in each corner. */
export function drawTabletFace(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  wood: CanvasImageSource | null,
  screws = true,
): void {
  ctx.save();
  if (wood) {
    ctx.drawImage(wood, 0, 0, w, h);
  } else {
    ctx.fillStyle = '#7a4a22';
    ctx.fillRect(0, 0, w, h);
  }
  // Bronze tint over the walnut, then a warm sheen.
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = 'rgba(214, 138, 62, 0.62)';
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'overlay';
  const sheen = ctx.createLinearGradient(0, 0, w, h);
  sheen.addColorStop(0, 'rgba(255, 220, 160, 0.35)');
  sheen.addColorStop(0.5, 'rgba(120, 60, 20, 0.15)');
  sheen.addColorStop(1, 'rgba(255, 200, 120, 0.25)');
  ctx.fillStyle = sheen;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'source-over';

  // Bevel: light from the top left, shadow bottom right.
  const rim = Math.round(Math.min(w, h) * 0.035);
  const edge = (x0: number, y0: number, x1: number, y1: number, color: string) => {
    const g = ctx.createLinearGradient(x0, y0, x1, y1);
    g.addColorStop(0, color);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
  };
  edge(0, 0, 0, rim, 'rgba(255, 235, 200, 0.45)');
  ctx.fillRect(0, 0, w, rim);
  edge(0, 0, rim, 0, 'rgba(255, 235, 200, 0.35)');
  ctx.fillRect(0, 0, rim, h);
  edge(0, h, 0, h - rim, 'rgba(0, 0, 0, 0.55)');
  ctx.fillRect(0, h - rim, w, rim);
  edge(w, 0, w - rim, 0, 'rgba(0, 0, 0, 0.45)');
  ctx.fillRect(w - rim, 0, rim, h);
  ctx.strokeStyle = 'rgba(40, 20, 5, 0.8)';
  ctx.lineWidth = 3;
  ctx.strokeRect(1.5, 1.5, w - 3, h - 3);

  if (!screws) {
    ctx.restore();
    return;
  }
  const inset = rim * 1.9;
  const r = Math.round(Math.min(w, h) * 0.022);
  for (const [x, y, a] of [
    [inset, inset, 0.4],
    [w - inset, inset, 1.9],
    [inset, h - inset, 2.6],
    [w - inset, h - inset, 0.9],
  ] as const)
    drawScrew(ctx, x, y, r, a);
  ctx.restore();
}

function drawScrew(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, slot: number) {
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.6)';
  ctx.shadowBlur = r * 0.8;
  ctx.shadowOffsetX = r * 0.15;
  ctx.shadowOffsetY = r * 0.25;
  const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.35, r * 0.1, x, y, r);
  g.addColorStop(0, '#ffffff');
  g.addColorStop(0.35, '#d9dde3');
  g.addColorStop(0.8, '#8b929c');
  g.addColorStop(1, '#4d535b');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = 'rgba(30, 32, 36, 0.7)';
  ctx.lineWidth = Math.max(1, r * 0.12);
  ctx.stroke();
  // The slot.
  ctx.strokeStyle = 'rgba(25, 27, 30, 0.85)';
  ctx.lineWidth = Math.max(1.5, r * 0.22);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x - Math.cos(slot) * r * 0.7, y - Math.sin(slot) * r * 0.7);
  ctx.lineTo(x + Math.cos(slot) * r * 0.7, y + Math.sin(slot) * r * 0.7);
  ctx.stroke();
  ctx.restore();
}

/** Gold with a bright band near the top, like polished metal. */
export function goldGradient(
  ctx: CanvasRenderingContext2D,
  y0: number,
  y1: number,
): CanvasGradient {
  const g = ctx.createLinearGradient(0, y0, 0, y1);
  g.addColorStop(0, '#fff6c8');
  g.addColorStop(0.28, '#ffd75e');
  g.addColorStop(0.55, '#c9932a');
  g.addColorStop(0.8, '#f1c250');
  g.addColorStop(1, '#8f6512');
  return g;
}

export type Align = 'left' | 'center' | 'right';

/**
 * Gold lettering. A `maxWidth` squeezes a line that would run past it (the canvas condenses the
 * glyphs), so larger text never spills over a plaque, a gem or the tablet's rim.
 */
export function drawGoldText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  size: number,
  align: Align = 'left',
  weight = 700,
  alpha = 1,
  outline = 0.07,
  maxWidth?: number,
  /**
   * Plain white instead of the gold gradient. Gold on a coloured stone is handsome up close and
   * muddy from across the table, where the letters and the gem they sit on are the same warm
   * range; white parts from every stone colour the tablet uses.
   */
  white = false,
  /** Leaning letters, for the small line that explains rather than names. */
  italic = false,
): void {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = `${italic ? 'italic ' : ''}${weight} ${size}px ${FONT}`;
  ctx.textAlign = align;
  ctx.textBaseline = 'alphabetic';
  ctx.lineJoin = 'round';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
  ctx.shadowBlur = size * 0.12;
  ctx.shadowOffsetY = size * 0.06;
  ctx.strokeStyle = 'rgba(50, 28, 4, 0.85)';
  ctx.lineWidth = Math.max(1, size * outline);
  if (maxWidth === undefined) ctx.strokeText(text, x, y);
  else ctx.strokeText(text, x, y, maxWidth);
  ctx.shadowColor = 'transparent';
  ctx.fillStyle = white ? '#ffffff' : goldGradient(ctx, y - size * 0.85, y + size * 0.15);
  if (maxWidth === undefined) ctx.fillText(text, x, y);
  else ctx.fillText(text, x, y, maxWidth);
  ctx.restore();
}

export function measureText(
  ctx: CanvasRenderingContext2D,
  text: string,
  size: number,
  weight = 700,
): number {
  ctx.font = `${weight} ${size}px ${FONT}`;
  return ctx.measureText(text).width;
}

/** A darker inset with gold lettering, as if burnt into the wood. */
export function drawPlaque(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  text: string,
  size: number,
): void {
  ctx.save();
  roundedRect(ctx, rect, rect.h / 2);
  ctx.fillStyle = 'rgba(25, 12, 2, 0.5)';
  ctx.fill();
  const inner = ctx.createLinearGradient(rect.x, rect.y, rect.x, rect.y + rect.h);
  inner.addColorStop(0, 'rgba(0,0,0,0.45)');
  inner.addColorStop(0.4, 'rgba(0,0,0,0)');
  inner.addColorStop(1, 'rgba(255,220,170,0.12)');
  ctx.fillStyle = inner;
  ctx.fill();
  ctx.restore();
  drawGoldText(
    ctx,
    text,
    rect.x + rect.w / 2,
    rect.y + rect.h * 0.7,
    size,
    'center',
    600,
    1,
    0.07,
    rect.w - size,
  );
}

/** Six little stones of the luminosity cycle; the current phase wears a gold ring. */
/** Centre to centre, the light cycle's stones stand this many of their radii apart. */
const STONE_PITCH = 2.6;

/** How wide a row of `count` cycle stones of radius `r` is drawn. */
export function cycleStonesWidth(count: number, r: number): number {
  return r * (2 + STONE_PITCH * (count - 1));
}

export function drawCycleStones(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  colors: readonly string[],
  phase: number,
): void {
  colors.forEach((color, i) => {
    const cx = x + i * r * STONE_PITCH + r;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = r * 0.6;
    ctx.shadowOffsetY = r * 0.2;
    const g = ctx.createRadialGradient(cx - r * 0.3, y - r * 0.3, r * 0.1, cx, y, r);
    g.addColorStop(0, 'rgba(255,255,255,0.95)');
    g.addColorStop(0.3, color);
    g.addColorStop(1, shade(color, 0.45));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    if (i === phase) {
      ctx.strokeStyle = goldGradient(ctx, y - r, y + r);
      ctx.lineWidth = Math.max(2, r * 0.35);
      ctx.stroke();
    }
    ctx.restore();
  });
}

export interface BalanceSegment {
  label: string;
  /** The faction's colour from the board data. */
  color: string;
  /** 0 … 1, all segments together fill the bar. */
  share: number;
}

/**
 * The score: a gold-rimmed trough divided between the factions in their own colours, each part
 * as wide as that side's share of the advantage, with the percentage written on it.
 */
export function drawBalance(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  segments: readonly BalanceSegment[],
  fontSize: number,
  /** A needle across the bar at this fraction of it, as a Card clash's chances are rolled. */
  needle?: number,
): void {
  const radius = rect.h / 2;
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.5)';
  ctx.shadowBlur = rect.h * 0.25;
  ctx.shadowOffsetY = rect.h * 0.08;
  roundedRect(ctx, rect, radius);
  ctx.fillStyle = goldGradient(ctx, rect.y, rect.y + rect.h);
  ctx.fill();
  ctx.shadowColor = 'transparent';
  const rim = Math.max(2, rect.h * 0.12);
  const trough: Rect = {
    x: rect.x + rim,
    y: rect.y + rim,
    w: rect.w - 2 * rim,
    h: rect.h - 2 * rim,
  };
  roundedRect(ctx, trough, radius - rim);
  ctx.clip();
  ctx.fillStyle = '#1a0d04';
  ctx.fillRect(trough.x, trough.y, trough.w, trough.h);
  let x = trough.x;
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.share), 0) || 1;
  for (const segment of segments) {
    const w = (trough.w * Math.max(0, segment.share)) / total;
    const body = ctx.createLinearGradient(0, trough.y, 0, trough.y + trough.h);
    body.addColorStop(0, mix(segment.color, '#ffffff', 0.35));
    body.addColorStop(0.5, segment.color);
    body.addColorStop(1, shade(segment.color, 0.55));
    ctx.fillStyle = body;
    ctx.fillRect(x, trough.y, w, trough.h);
    x += w;
  }
  // A dark seam between the parts and a glossy top.
  x = trough.x;
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  for (const segment of segments.slice(0, -1)) {
    x += (trough.w * Math.max(0, segment.share)) / total;
    ctx.fillRect(x - 1.5, trough.y, 3, trough.h);
  }
  const gloss = ctx.createLinearGradient(0, trough.y, 0, trough.y + trough.h * 0.5);
  gloss.addColorStop(0, 'rgba(255,255,255,0.32)');
  gloss.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gloss;
  ctx.fillRect(trough.x, trough.y, trough.w, trough.h * 0.5);
  ctx.restore();

  x = trough.x;
  for (const segment of segments) {
    const w = (trough.w * Math.max(0, segment.share)) / total;
    const percent = `${Math.round((100 * Math.max(0, segment.share)) / total)} %`;
    const text =
      measureText(ctx, `${segment.label} ${percent}`, fontSize, 800) < w - fontSize
        ? `${segment.label} ${percent}`
        : measureText(ctx, percent, fontSize, 800) < w - fontSize * 0.5
          ? percent
          : '';
    if (text)
      drawGoldText(
        ctx,
        text,
        x + w / 2,
        rect.y + rect.h / 2 + fontSize * 0.36,
        fontSize,
        'center',
        800,
        1,
        0.13,
      );
    x += w;
  }
  if (needle !== undefined) {
    const nx = rect.x + rim + (rect.w - 2 * rim) * Math.min(1, Math.max(0, needle));
    ctx.save();
    ctx.shadowColor = '#ffffff';
    ctx.shadowBlur = rect.h * 0.3;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(nx - 3, rect.y - rect.h * 0.12, 6, rect.h * 1.24);
    ctx.restore();
  }
}

/**
 * A gemstone set in a gold bezel: translucent so the wood shows through, smoke drifting inside,
 * facets catching the light and a couple of twinkling sparkles.
 */
export function drawGem(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  kind: GemKind,
  o: GemOptions,
): void {
  const c = o.enabled ? GEM_COLORS[kind] : DULL;
  const radius = o.flat ? 2 : Math.min(rect.h / 2, rect.w / 4);
  ctx.save();
  // Bezel and drop shadow.
  ctx.shadowColor = o.flat ? 'transparent' : 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = o.flat ? 0 : rect.h * 0.18;
  ctx.shadowOffsetY = o.flat ? 0 : rect.h * 0.07;
  roundedRect(ctx, rect, radius);
  ctx.fillStyle = o.active ? '#ffe27a' : o.enabled ? '#b98a2e' : '#6a5a3a';
  ctx.fill();
  ctx.shadowColor = 'transparent';
  const bezel = Math.max(2, rect.h * 0.075);
  const stone: Rect = {
    x: rect.x + bezel,
    y: rect.y + bezel,
    w: rect.w - 2 * bezel,
    h: rect.h - 2 * bezel,
  };
  const sr = Math.max(0, radius - bezel);

  // The stone itself, translucent over the wood.
  roundedRect(ctx, stone, sr);
  ctx.save();
  ctx.clip();
  ctx.globalAlpha = o.enabled ? 0.82 : 0.7;
  const body = ctx.createLinearGradient(stone.x, stone.y, stone.x, stone.y + stone.h);
  body.addColorStop(0, c.light);
  body.addColorStop(0.45, c.mid);
  body.addColorStop(1, c.dark);
  ctx.fillStyle = body;
  ctx.fillRect(stone.x, stone.y, stone.w, stone.h);
  ctx.globalAlpha = 1;

  if (o.enabled && !o.flat) {
    // Smoke: soft glows wandering around inside the stone.
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < 3; i++) {
      const p = o.seed * 1.7 + i * 2.1;
      const px = stone.x + stone.w * (0.5 + 0.35 * Math.sin(o.time * 0.37 + p));
      const py = stone.y + stone.h * (0.5 + 0.3 * Math.cos(o.time * 0.29 + p * 1.3));
      const g = ctx.createRadialGradient(px, py, 0, px, py, stone.h * 0.7);
      g.addColorStop(0, hexAlpha(c.glow, 0.22));
      g.addColorStop(1, hexAlpha(c.glow, 0));
      ctx.fillStyle = g;
      ctx.fillRect(stone.x, stone.y, stone.w, stone.h);
    }
    ctx.globalCompositeOperation = 'source-over';
  }

  // Facets: a bright table across the top, a darker girdle below.
  if (!o.flat) {
    ctx.fillStyle = 'rgba(255,255,255,0.18)';
    ctx.beginPath();
    ctx.moveTo(stone.x, stone.y);
    ctx.lineTo(stone.x + stone.w, stone.y);
    ctx.lineTo(stone.x + stone.w * 0.86, stone.y + stone.h * 0.42);
    ctx.lineTo(stone.x + stone.w * 0.14, stone.y + stone.h * 0.42);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = 'rgba(0,0,0,0.2)';
    ctx.beginPath();
    ctx.moveTo(stone.x, stone.y + stone.h);
    ctx.lineTo(stone.x + stone.w, stone.y + stone.h);
    ctx.lineTo(stone.x + stone.w * 0.86, stone.y + stone.h * 0.62);
    ctx.lineTo(stone.x + stone.w * 0.14, stone.y + stone.h * 0.62);
    ctx.closePath();
    ctx.fill();
    // Specular highlight from the light above.
    const spec = ctx.createRadialGradient(
      stone.x + stone.w * 0.22,
      stone.y + stone.h * 0.2,
      0,
      stone.x + stone.w * 0.22,
      stone.y + stone.h * 0.2,
      stone.h * 0.6,
    );
    spec.addColorStop(0, 'rgba(255,255,255,0.55)');
    spec.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = spec;
    ctx.fillRect(stone.x, stone.y, stone.w, stone.h);
  }

  if (o.enabled && !o.flat) {
    for (let i = 0; i < 3; i++) {
      const p = o.seed * 3.1 + i * 1.9;
      const twinkle = Math.max(0, Math.sin(o.time * (1.6 + i * 0.5) + p));
      if (twinkle < 0.35) continue;
      const sx = stone.x + stone.w * (0.15 + 0.7 * frac(p * 0.731));
      const sy = stone.y + stone.h * (0.2 + 0.6 * frac(p * 0.417));
      drawSparkle(ctx, sx, sy, stone.h * (0.12 + 0.1 * twinkle), twinkle);
    }
  }
  if (o.pressed && o.pressed > 0) {
    ctx.fillStyle = `rgba(255,255,255,${0.45 * o.pressed})`;
    ctx.fillRect(stone.x, stone.y, stone.w, stone.h);
  }
  ctx.restore();

  // Inner rim so the stone reads as set into the bezel.
  roundedRect(ctx, stone, sr);
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.lineWidth = Math.max(1, rect.h * 0.03);
  ctx.stroke();
  ctx.restore();

  const size = o.fontSize ?? rect.h * 0.42;
  const badge = o.cost !== undefined && o.cost > 0 ? costBadgeWidth(size) : 0;
  // Capitals, because the tablet is read at a glance from a metre away and across a table: at
  // this size lower case loses its shape long before capitals do.
  drawGoldText(
    ctx,
    o.label.toUpperCase(),
    rect.x + (rect.w - badge) / 2,
    rect.y + rect.h / 2 + size * 0.36,
    size,
    'center',
    800,
    o.enabled ? 1 : 0.55,
    0.13,
    stone.w - size * 0.5 - badge,
    true,
  );
  if (o.cost !== undefined && o.cost > 0)
    drawCostBadge(
      ctx,
      rect.x + rect.w - badge / 2 - size * 0.35,
      rect.y + rect.h / 2,
      size,
      o.cost,
      o.enabled,
    );
}

/** How much room the price takes beside a name, so the name can be centred in what is left. */
function costBadgeWidth(size: number): number {
  return size * 1.6;
}

/** The price's digit is as tall as the name beside it, which is no smaller than the headset reads. */
export const COST_DIGIT_SCALE = 1;
/** The bulb's "/ most" under its number, as a share of the bulb's radius. */
export const BULB_SUB_SCALE = 0.55;

/**
 * What a spell costs, in a white disc beside its name. Smaller than the name and in red, so it
 * reads as a price rather than part of the title; both go quiet on a gem that cannot be pressed,
 * because a price nobody can pay should not be the brightest thing on the tablet.
 */
function drawCostBadge(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  cost: number,
  enabled: boolean,
): void {
  const r = size * 0.62;
  ctx.save();
  ctx.globalAlpha = enabled ? 1 : 0.45;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = enabled ? '#ffffff' : '#cfc8bd';
  ctx.fill();
  ctx.lineWidth = Math.max(1.5, size * 0.09);
  ctx.strokeStyle = enabled ? '#12100c' : 'rgba(18, 16, 12, 0.6)';
  ctx.stroke();
  ctx.font = `800 ${size * COST_DIGIT_SCALE}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = enabled ? '#c0392b' : '#8d5b52';
  ctx.fillText(String(cost), cx, cy + size * 0.03);
  ctx.restore();
}

/**
 * A lit glass bulb holding a number: the magic the selected piece is carrying, over the most it
 * could. Glass rather than a gem, so it does not read as something to press — nothing on this
 * panel that is only telling you a number should look like a button.
 */
export function drawBulb(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  text: string,
  sub: string,
  lit: boolean,
  scale = 1,
): void {
  ctx.save();
  // The glass, with the light inside it when there is magic left to hold.
  const glass = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.35, r * 0.1, cx, cy, r);
  glass.addColorStop(0, lit ? 'rgba(255, 236, 170, 0.95)' : 'rgba(150, 150, 160, 0.45)');
  glass.addColorStop(0.6, lit ? 'rgba(196, 139, 255, 0.55)' : 'rgba(90, 90, 105, 0.4)');
  glass.addColorStop(1, 'rgba(18, 14, 26, 0.75)');
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = glass;
  ctx.fill();
  ctx.lineWidth = Math.max(2, r * 0.09);
  ctx.strokeStyle = lit ? 'rgba(255, 226, 122, 0.85)' : 'rgba(120, 118, 130, 0.7)';
  ctx.stroke();

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `800 ${r * 0.86 * scale}px ${FONT}`;
  ctx.fillStyle = lit ? '#ffffff' : 'rgba(255, 255, 255, 0.6)';
  ctx.fillText(text, cx, cy - (sub ? r * 0.16 : 0));
  if (sub) {
    ctx.font = `700 ${r * BULB_SUB_SCALE * scale}px ${FONT}`;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.fillText(sub, cx, cy + r * 0.5);
  }
  ctx.restore();
}

function drawSparkle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, a: number) {
  ctx.save();
  ctx.globalAlpha = Math.min(1, a);
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const ang = (i * Math.PI) / 4;
    const len = i % 2 === 0 ? r : r * 0.28;
    ctx.lineTo(x + Math.cos(ang) * len, y + Math.sin(ang) * len);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

export function roundedRect(ctx: CanvasRenderingContext2D, r: Rect, radius: number): void {
  const rr = Math.min(radius, r.w / 2, r.h / 2);
  ctx.beginPath();
  ctx.moveTo(r.x + rr, r.y);
  ctx.lineTo(r.x + r.w - rr, r.y);
  ctx.quadraticCurveTo(r.x + r.w, r.y, r.x + r.w, r.y + rr);
  ctx.lineTo(r.x + r.w, r.y + r.h - rr);
  ctx.quadraticCurveTo(r.x + r.w, r.y + r.h, r.x + r.w - rr, r.y + r.h);
  ctx.lineTo(r.x + rr, r.y + r.h);
  ctx.quadraticCurveTo(r.x, r.y + r.h, r.x, r.y + r.h - rr);
  ctx.lineTo(r.x, r.y + rr);
  ctx.quadraticCurveTo(r.x, r.y, r.x + rr, r.y);
  ctx.closePath();
}

export function inRect(r: Rect, x: number, y: number): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

function frac(v: number): number {
  return v - Math.floor(v);
}

function hexAlpha(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

function mix(hex: string, towards: string, k: number): string {
  const a = parseInt(hex.slice(1), 16);
  const b = parseInt(towards.slice(1), 16);
  const f = (shift: number) =>
    Math.round(((a >> shift) & 255) * (1 - k) + ((b >> shift) & 255) * k);
  return `rgb(${f(16)}, ${f(8)}, ${f(0)})`;
}

function shade(hex: string, k: number): string {
  const n = parseInt(hex.slice(1), 16);
  const f = (v: number) => Math.round(v * k);
  return `rgb(${f((n >> 16) & 255)}, ${f((n >> 8) & 255)}, ${f(n & 255)})`;
}
